// AdminPanel.jsx - Con Unidades Organizativas
import React, { useState, useEffect, useContext } from 'react';
import {
  Users,
  UserCog,
  Settings,
  Shield,
  AlertCircle,
  RefreshCw,
  Activity,
  BarChart3,
  Database
} from 'lucide-react';
import { AuthContext } from '../../AuthContext';
import { db, collection, getDocs } from '../../firebase';
import UnifiedUserManagement from './UnifiedUserManagement';
import RoleManagement from './RoleManagement';
import PermissionManagement from './PermissionManagement';
import GroupManagement from './GroupManagement';
import styles from '../CSS/AdminPanel.module.css';

const AdminPanel = () => {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organizationalUnits, setOrganizationalUnits] = useState([]); // ⭐ NUEVO
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  // ⭐ URL del servidor (ajusta según tu configuración)
  const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'https://truebavirtual.onrender.com';

  // Permisos disponibles en la aplicación
  const availablePermissions = [
    { id: 'gestion', name: 'Gestión', description: 'Acceso al módulo de gestión general' },
    { id: 'correos', name: 'Gestión de Correos', description: 'Gestión de correos electrónicos' },
    { id: 'clases', name: 'Clases', description: 'Acceso al módulo de clases' },
    { id: 'reservas', name: 'Reservas', description: 'Sistema de reservas' },
    { id: 'incidencias', name: 'Incidencias', description: 'Sistema de tickets/incidencias' },
    { id: 'admin', name: 'Administración', description: 'Panel de administración completo' }
  ];

  useEffect(() => {
    fetchData();
    fetchOrganizationalUnits(); // ⭐ NUEVO
  }, []);

  useEffect(() => {
    if (feedback.message) {
      const timer = setTimeout(() => {
        setFeedback({ type: "", message: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Cargar usuarios
      const usersSnapshot = await getDocs(collection(db, "users"));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Cargar roles
      const rolesSnapshot = await getDocs(collection(db, "roles"));
      const rolesList = rolesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setUsers(usersList);
      setRoles(rolesList);
    } catch (error) {
      showFeedback("error", "Error al cargar datos: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ⭐ NUEVA FUNCIÓN: Cargar unidades organizativas
  const fetchOrganizationalUnits = async () => {
    try {
      console.log('📦 Cargando Unidades Organizativas...');
      
      const response = await fetch(`${SERVER_URL}/api/workspace/data`);
      if (!response.ok) {
        throw new Error('Error obteniendo datos de Google Workspace');
      }
      
      const data = await response.json();
      const units = data.organizationUnits || [];
      
      // Organizar en jerarquía
      const organizedUnits = organizeOUsHierarchy(units);
      setOrganizationalUnits(organizedUnits);
      
      console.log('✅ Unidades Organizativas cargadas:', organizedUnits.length);
    } catch (error) {
      console.error('❌ Error cargando OUs:', error);
      // No mostrar error al usuario si falla, ya que no es crítico para otras funciones
      setOrganizationalUnits([]);
    }
  };

  // ⭐ NUEVA FUNCIÓN: Organizar OUs en jerarquía
  const organizeOUsHierarchy = (ous) => {
    const ouMap = new Map();
    const rootOUs = [];
    
    // Crear mapa de todas las OUs
    ous.forEach(ou => {
      ouMap.set(ou.orgUnitPath, {
        ...ou,
        children: []
      });
    });
    
    // Construir jerarquía
    ous.forEach(ou => {
      const ouData = ouMap.get(ou.orgUnitPath);
      const pathParts = ou.orgUnitPath.split('/').filter(part => part !== '');
      
      if (pathParts.length === 1) {
        // OU raíz
        rootOUs.push(ouData);
      } else {
        // OU hija - buscar padre
        const parentPath = '/' + pathParts.slice(0, -1).join('/');
        const parent = ouMap.get(parentPath);
        if (parent) {
          parent.children.push(ouData);
        }
      }
    });
    
    return rootOUs;
  };

  const showFeedback = (type, message) => {
    setFeedback({ type, message });
  };

  // Obtener estadísticas del sistema
  const getSystemStats = () => {
    const totalUsers = users.length;
    const adminUsers = users.filter(u => u.role === 'admin').length;
    const activeUsers = users.filter(u => !u.suspended).length;
    const totalRoles = roles.length;
    const totalOUs = organizationalUnits.length; // ⭐ NUEVO

    return { totalUsers, adminUsers, activeUsers, totalRoles, totalOUs };
  };

  const stats = getSystemStats();

  const tabs = [
    {
      id: 'users',
      name: 'Usuarios',
      icon: <Users size={18} />,
      badge: stats.totalUsers,
      component: (
        <UnifiedUserManagement 
          users={users}
          roles={roles}
          currentUser={user}
          onUpdate={fetchData}
          showFeedback={showFeedback}
        />
      )
    },
    {
      id: 'roles',
      name: 'Roles',
      icon: <UserCog size={18} />,
      badge: stats.totalRoles,
      component: (
        <RoleManagement 
          roles={roles}
          availablePermissions={availablePermissions}
          organizationalUnits={organizationalUnits} // ⭐ PASAR AQUÍ
          onUpdate={fetchData}
          showFeedback={showFeedback}
          currentUser={user}
          users={users}
        />
      )
    },
    {
      id: 'groups',
      name: 'Grupos',
      icon: <Users size={18} />,
      badge: null,
      component: (
        <GroupManagement
          users={users}
          roles={roles}
          availablePermissions={availablePermissions}
          organizationalUnits={organizationalUnits}
          onUpdate={fetchData}
          showFeedback={showFeedback}
          currentUser={user}
        />
      )
    },
    {
      id: 'permissions',
      name: 'Permisos',
      icon: <Settings size={18} />,
      badge: availablePermissions.length,
      component: (
        <PermissionManagement
          permissions={availablePermissions}
          showFeedback={showFeedback}
        />
      )
    }
  ];

  return (
    <div className={styles.container}>
      {/* Header minimalista con métricas */}
      <div className={styles.header}>
        <div className={styles.metricsBar}>
          {/* Status online a la izquierda */}
          <div className={styles.statusGroup}>
            <div className={styles.statusIndicator}>
              <div className={`${styles.statusDot} ${styles.online}`}></div>
              <span className={styles.onlineText}>Online</span>
            </div>
          </div>

          {/* Métricas en el centro */}
          <div className={styles.statsRow}>
            <div className={styles.statItem}>
              <Users size={16} />
              <span className={styles.statNumber}>{stats.totalUsers}</span>
              <span className={styles.statLabel}>usuarios</span>
            </div>
            <div className={styles.statItem}>
              <Shield size={16} />
              <span className={styles.statNumber}>{stats.adminUsers}</span>
              <span className={styles.statLabel}>admins</span>
            </div>
            <div className={styles.statItem}>
              <Activity size={16} />
              <span className={styles.statNumber}>{stats.activeUsers}</span>
              <span className={styles.statLabel}>activos</span>
            </div>
            <div className={styles.statItem}>
              <UserCog size={16} />
              <span className={styles.statNumber}>{stats.totalRoles}</span>
              <span className={styles.statLabel}>roles</span>
            </div>
          </div>

          {/* Botón refresh a la derecha */}
          <div className={styles.actionsGroup}>
            <button
              className={styles.refreshButton}
              onClick={() => {
                fetchData();
                fetchOrganizationalUnits();
              }}
              disabled={loading}
              title="Actualizar datos"
            >
              <RefreshCw size={16} className={loading ? styles.spinning : ''} />
            </button>
          </div>
        </div>
      </div>

      {/* Navegación horizontal compacta */}
      <div className={styles.navigation}>
        <div className={styles.tabs}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <div className={styles.tabIconContainer}>
                <div className={styles.tabIcon}>{tab.icon}</div>
              </div>
              <span className={styles.tabName}>{tab.name}</span>
              {tab.badge > 0 && (
                <span className={styles.tabBadge}>{tab.badge}</span>
              )}
              <div className={styles.tabEffect}></div>
            </button>
          ))}
        </div>
      </div>

      {/* Contenido principal */}
      <div className={styles.content}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <span>Cargando...</span>
          </div>
        ) : (
          <div className={styles.tabContent}>
            {tabs.find(tab => tab.id === activeTab)?.component}
          </div>
        )}
      </div>

      {/* Notificaciones */}
      {feedback.message && (
        <div className={`${styles.notification} ${styles[feedback.type]}`}>
          <AlertCircle size={16} />
          <span>{feedback.message}</span>
          <button 
            className={styles.closeBtn}
            onClick={() => setFeedback({ type: "", message: "" })}
          >
            ×
          </button>
        </div>
      )}

      {/* Footer minimalista */}
      <div className={styles.footer}>
        <div className={styles.footerContent}>
          <span><Database size={14} /> Conectado</span>
          <span><BarChart3 size={14} /> {stats.totalUsers} usuarios</span>
          <span><Activity size={14} /> Última sync: {new Date().toLocaleTimeString('es-ES')}</span>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;